import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledTextInput,
    Form,
    isWeb,
    Sheet,
    StyledH2Input,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

const inviteFormSchema = z.object({
    email: z.string().email('Please enter a valid email address.'),
});

type InviteFormSchema = z.infer<typeof inviteFormSchema>;

interface InviteJointOwnerSheetProps {
    isOpen: boolean;
    onClose: (email?: string) => void;
}

const InviteJointOwnerSheet = ({
    isOpen,
    onClose,
}: InviteJointOwnerSheetProps) => {
    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<InviteFormSchema>({
        resolver: zodResolver(inviteFormSchema),
        defaultValues: {
            email: '',
        },
    });

    const close = () => {
        reset();
        onClose();
    };

    const onSubmit = ({ email }: InviteFormSchema) => {
        onClose(email);
        reset();
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={close}
            title="Joint owner email"
            showCloseButton={true}
        >
            <YStack>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <ControlledTextInput
                        Input={StyledH2Input}
                        name="email"
                        control={control}
                        register={register}
                        errors={errors}
                        placeholder="Email address"
                        borderWidth={0}
                        textAlign="center"
                        autoCapitalize="none"
                        autoCorrect={false}
                        enablesReturnKeyAutomatically
                        returnKeyType="done"
                        onSubmitEditing={() => {
                            void handleSubmit(onSubmit)();
                        }}
                    />
                    <BodySmallRegular
                        textAlign="center"
                        color="$gray900"
                        marginVertical={12}
                        paddingHorizontal={32}
                    >
                        Double check for any typos before we send an invite to
                        this email address.
                    </BodySmallRegular>
                    {isWeb && (
                        <XStack marginTop={32} width="100%">
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                tone="positive"
                                size={ButtonSize.LARGE}
                                text="Save"
                                onPress={handleSubmit(onSubmit)}
                            />
                        </XStack>
                    )}
                </Form>
            </YStack>
        </Sheet>
    );
};

export default InviteJointOwnerSheet;
